import React, { useState, useEffect } from "react";
import { Search, Filter, ChevronDown, MoreVertical } from "lucide-react";
import { ADGetTickets } from "../Services/AllTicketsService";
import NotificationAlert from "../../../../Alerts/NotificationAlert";
import DropdownPortal from "../../../../DropDownPortal";
import ADReviewInProgressTicketModal from "../Modals/ADReviewInProgressTicketModal";
import ADHoldTicketModal from "../Modals/ADHoldTicketModal";
import ViewTicketModal from "../../../../Modal/ViewTicketModal";
import TicketHistoryModal from "../../../../Modal/TicketHistoryModal";
import FilterTableModal from "../../../../Modal/FilterTableModal";
import { ChevronLeft, ChevronRight } from "lucide-react";
 import { useNavigate } from "react-router-dom";
 import TableHeader from "../../../../Common/TableHeader";
import Pagination from "../../../../Common/Pagination";
import { globalSearch } from "../../../../Utils/globalSearch";

const getSeverityStyle = (severity) => {
  switch (severity) {
    case "High":
      return "bg-red-100 text-red-700";
    case "Moderate":
      return "bg-yellow-100 text-yellow-700";
    case "Low":
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const ADAssignToReviewerTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [alert, setAlert] = useState(null);

  // Dropdown UI states
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // Modal States
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdTicketData, setHoldTicketData] = useState(null);
  const [showReviewInProgressModal, setShowReviewInProgressModal] =
    useState(false);
  const [reviewInProgressTicketData, setReviewInProgressTicketData] =
    useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [historyModal, setHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  //Filter Modal States
  const [filterModal, setFilterModal] = useState(false);
  const [filterField, setFilterField] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [filters, setFilters] = useState({});
  const [filteredTickets, setFilteredTickets] = useState([]);

  //Search States
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Load Only New Tickets
  useEffect(() => {
    loadNewTickets();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters, filteredTickets, rowsPerPage]);

  const loadNewTickets = async () => {
    try {
      const response = await ADGetTickets(12);
      const data = response.data;

      if (data.status === "Success") {
        const formatted = data.tickets
          .filter((t) => t.status === "Assign To Reviewer")
          .map((t, index) => ({
            id: index + 1,
            title: t.title,
            description: t.description,
            severity: t.severity,
            severityByPM: t.severityByPM,
            ticketType: t.ticketType,
            status: t.status,
            ticketNumber: t.ticketNumber,
            projectName: t.projectName,
            raisedByEmail: t.raisedByEmail,
            assignedBy: t.assignedBy,
            assignedTo: t.assignedTo,
            raisedAt: t.raisedAt,
            updated: formatDateTime(t.raisedAt),
            approvedAt: t.approvedAt,
            estimatedCompleteByDev: t.estimatedCompleteByDev,
            closeDate: t.closeDate,
            ...t,
          }));

        setTickets(formatted);
        setFilteredTickets(formatted);
      }
    } catch (err) {
       setAlert({
        type: "error",
        message: err?.response?.data?.message || "Failed to load tickets."
       });
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" }); // Jan, Feb, Mar...
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12; // convert 0 → 12

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  // Close action menu on outside click
  useEffect(() => {
    const closeMenu = () => setOpenActionMenu(null);
    window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, []);

  const getUniqueValues = (key) => {
    const values = tickets
      .map((t) => t[key])
      .filter((v) => v !== null && v !== undefined && v !== "");

    return [...new Set(values)];
  };

  const openFilter = (field) => {
    setFilterField(field);
    setFilterOptions(getUniqueValues(field));
    setFilterModal(true);
  };

  const applyColumnFilter = (field, selectedValues) => {
    const updated = { ...filters, [field]: selectedValues };
    setFilters(updated);

    let result = tickets;

    // Apply each active filter
    Object.keys(updated).forEach((key) => {
      if (updated[key].length > 0) {
        result = result.filter((row) => updated[key].includes(row[key]));
      }
    });

    // If search is active → apply search as well
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();

      result = result.filter((t) =>
        (
          t.title +
          " " +
          t.description +
          " " +
          t.projectName +
          " " +
          t.ticketType +
          " " +
          t.assignedTo +
          " " +
          t.status +
          " " +
          t.severity
        )
          .toLowerCase()
          .includes(q)
      );
    }

    setFilteredTickets(result);
  };

  //Search function
   const handleSearch = (query) => {
  setSearchQuery(query);
  setFilteredTickets(globalSearch(tickets, query));
};

  const rowsToShow =
    searchQuery.trim() !== "" ||
    Object.values(filters).some((arr) => arr.length > 0)
      ? filteredTickets
      : tickets;

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const currentRows = rowsToShow.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(rowsToShow.length / rowsPerPage);

    const startIndex = rowsToShow.length === 0 ? 0 : indexOfFirstRow + 1;
  const endIndex = Math.min(indexOfLastRow, rowsToShow.length);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}

      {/* Header */}
    <TableHeader
        searchQuery={searchQuery}
        onSearch={handleSearch}
        placeholder="Search for tickets"
        totalRecords={rowsToShow.length}
        startIndex={startIndex}
        endIndex={endIndex}
        rowsPerPage={rowsPerPage}
        onPageSizeChange={setRowsPerPage}
      />

      {/* TABLE */}
      <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
            <tr>
              <th className="p-3">Ticket No.</th>
              <th className="p-3">
                <div className="flex items-center gap-1">
                  Project Name
                  <Filter
                    size={14}
                    className="cursor-pointer text-gray-500 hover:text-black"
                    onClick={() => openFilter("projectName")}
                  />
                  {filters["projectName"]?.length > 0 && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {filters["projectName"].length}
                    </span>
                  )}
                </div>
              </th>
              {/* <th className="p-3">Last Updated</th> */}
              <th className="p-3">
                <div className="flex items-center gap-1">
                  Assigned To
                  <Filter
                    size={14}
                    className="cursor-pointer text-gray-500 hover:text-black"
                    onClick={() => openFilter("assignedTo")}
                  />
                  {filters["assignedTo"]?.length > 0 && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {filters["assignedTo"].length}
                    </span>
                  )}
                </div>
              </th>
              <th className="p-3">
                <div className="flex items-center gap-1">
                  Ticket Type
                  <Filter
                    size={14}
                    className="cursor-pointer text-gray-500 hover:text-black"
                    onClick={() => openFilter("ticketType")}
                  />
                  {filters["ticketType"]?.length > 0 && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {filters["ticketType"].length}
                    </span>
                  )}
                </div>
              </th>
              <th className="p-3">
                <div className="flex items-center gap-1">
                  Priority
                  <Filter
                    size={14}
                    className="cursor-pointer text-gray-500 hover:text-black"
                    onClick={() => openFilter("severity")}
                  />
                  {filters["severity"]?.length > 0 && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {filters["severity"].length}
                    </span>
                  )}
                </div>
              </th>
              <th className="p-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {currentRows.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-6 text-center text-gray-500">
                  No Assign To Reviewer tickets found.
                </td>
              </tr>
            ) : (
              currentRows.map((ticket) => (
                <tr
                  key={ticket.ticketNumber}
                  className="border-b hover:bg-gray-50 transition"
                >
                  <td
                    className="p-3 font-medium text-blue-600 cursor-pointer hover:underline"
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setViewModal(true);
                    }}
                  >
                    {ticket.ticketNumber}
                  </td>

                  <td className="p-3">{ticket.projectName}</td>
                  {/* <td className="p-3">{ticket.updated}</td> */}
                  <td className="p-3">{ticket.assignedTo || "--"}</td>
                  <td className="p-3">{ticket.ticketType}</td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityStyle(
                        ticket.severity
                      )}`}
                    >
                      {ticket.severity}
                    </span>
                  </td>

                  {/* ACTION BUTTON */}
                  <td className="p-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenu(
                          openActionMenu === ticket.id ? null : ticket.id
                        );
                        setMenuPosition({ x: e.clientX, y: e.clientY });
                      }}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <MoreVertical size={18} />
                    </button>
                  </td>

                  {/* DROPDOWN PORTAL */}
                  {openActionMenu === ticket.id && (
                    <DropdownPortal>
                      <div
                        style={{
                          position: "fixed",
                          top: menuPosition.y + 10,
                          left: menuPosition.x - 160,
                          zIndex: 99999,
                        }}
                        className="w-44 bg-white border rounded-lg shadow-xl py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowReviewInProgressModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          Review In Progress
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowHoldModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          Hold
                        </button>
                      </div>
                    </DropdownPortal>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => setCurrentPage(page)}
      />
      
      {/* View Details of Ticket Modal */}
      <ViewTicketModal
        show={viewModal}
        onClose={() => setViewModal(false)}
        onOpenHistory={(history) => {
          setViewModal(false);
          setHistoryData(history);
          setHistoryModal(true);
        }}
        ticket={selectedTicket}
        onHold={(ticket) => {
          setHoldTicketData(ticket);
          setShowHoldModal(true);
        }}
        onReviewInProgress={(ticket) => {
          setReviewInProgressTicketData(ticket);
          setShowReviewInProgressModal(true);
        }}
      />

      <TicketHistoryModal
        show={historyModal}
        onClose={() => {
          setHistoryModal(false);
          setViewModal(true);
        }}
        history={historyData}
      />

      <FilterTableModal
        show={filterModal}
        onClose={() => setFilterModal(false)}
        field={filterField}
        options={filterOptions}
        activeSelections={filters[filterField] || []}
        onApply={applyColumnFilter}
        onResetAll={() => {
          setFilters({});
          setFilteredTickets(tickets);
        }}
      />

      {/* Attach Modals */}
      <ADReviewInProgressTicketModal
        show={showReviewInProgressModal}
        onClose={() => setShowReviewInProgressModal(false)}
        ticketData={selectedTicket}
        onReviewInProgress={loadNewTickets}
      />
      <ADHoldTicketModal
        show={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        ticketData={selectedTicket}
        onHold={loadNewTickets}
      />
    </div>
  );
};

export default ADAssignToReviewerTickets;
